import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store';
import { applicationsApi } from '../../api/endpoints/applications.api';
import { statsApi } from '../../api/endpoints/stats.api';
import { DashboardConfig, DashboardCardConfig, CardStatistics } from 'types/dashboard.types';

interface DashboardState {
  config: DashboardConfig | null;
  cardsData: Record<string, CardStatistics>;
  loading: Record<string, boolean>;
  errors: Record<string, string | null>;
}

const initialState: DashboardState = {
  config: null,
  cardsData: {},
  loading: {},
  errors: {},
};

const LOCAL_STORAGE_KEY = 'dashboard_config';

const getDefaultConfig = (userId: number): DashboardConfig => ({
  userId,
  cards: [
    {
      id: 'card-1',
      type: 'push_stats',
      appId: null,
      position: { x: 0, y: 0, w: 6, h: 4 },
    },
    {
      id: 'card-2',
      type: 'user_stats',
      appId: null,
      position: { x: 6, y: 0, w: 6, h: 4 },
    },
  ],
  gridCols: 12,
  lastUpdated: new Date().toISOString(),
});

export const loadDashboardConfig = createAsyncThunk(
  'dashboard/loadConfig',
  async (userId: number) => {
    try {
      const storedConfigs = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (storedConfigs) {
        const configs = JSON.parse(storedConfigs);
        const userConfig = configs[userId];
        if (userConfig) {
          return userConfig;
        }
      }
    } catch (error) {
      console.error('Error loading dashboard config:', error);
    }
    
    return getDefaultConfig(userId);
  }
);

export const saveDashboardConfig = createAsyncThunk(
  'dashboard/saveConfig',
  async (config: DashboardConfig) => {
    try {
      const storedConfigs = localStorage.getItem(LOCAL_STORAGE_KEY);
      const configs = storedConfigs ? JSON.parse(storedConfigs) : {};
      
      configs[config.userId] = {
        ...config,
        lastUpdated: new Date().toISOString(),
      };
      
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(configs));
      return config;
    } catch (error) {
      console.error('Error saving dashboard config:', error);
      throw error;
    }
  }
);

export const fetchCardData = createAsyncThunk(
  'dashboard/fetchCardData',
  async ({ cardId, type, appId }: { cardId: string; type: string; appId: number }) => {
    let transformedData: CardStatistics = {
      appId,
      appName: '',
    };

    try {
      switch (type) {
        case 'push_stats': {
          const dashboardData = await statsApi.getDashboard(appId);
          const pushStats = dashboardData?.push_stats;
          if (pushStats) {
            transformedData.pushStats = {
              sent: pushStats.total_sent || 0,
              delivered: pushStats.total_delivered || 0,
              clicked: pushStats.total_clicked || 0,
              ctr: pushStats.avg_ctr || 0,
              trend: 0,
            };
          }
          break;
        }
        
        case 'user_stats': {
          const stats = await applicationsApi.getUserStats(appId);
          
          transformedData.userStats = {
            total: stats.total_users || 0,
            active: stats.active_users || 0,
            new: stats.with_registration || 0,
            trend: 0,
          };
          break;
        }

        case 'ctr': {
          const dashboardData = await statsApi.getDashboard(appId);
          const pushStats = dashboardData?.push_stats;

          if (pushStats) {
            const totalDelivered = pushStats.total_delivered || 0;
            const totalClicked = pushStats.total_clicked || 0;
            const totalSent = pushStats.total_sent || 1;

            transformedData.ctrStats = {
              ctr: pushStats.avg_ctr || 0,
              delivered: totalDelivered,
              clicked: totalClicked,
              averagePerPush: totalSent > 0 ? totalClicked / totalSent : 0,
            };
          }
          break;
        }
        
        case 'app_stats': {
          const app = await applicationsApi.get(appId);
          const userStats = await applicationsApi.getUserStats(appId);
          let totalPushes = 0;
          try {
            const dashboardData = await statsApi.getDashboard(appId);
            totalPushes = dashboardData?.push_stats?.total_sent || 0;
          } catch {
      
          }

          transformedData.appStats = {
            name: app.name,
            bundleId: app.bundle_id,
            totalUsers: userStats.total_users || 0,
            totalPushes,
            isActive: true,
          };
          transformedData.appName = app.name;
          break;
        }
        
        case 'conversions': {
          const result = await statsApi.getConversions(appId);
          const conversionData = (result && typeof result === 'object' && 'conversions' in (result as any))
            ? (result as any).conversions
            : result;

          transformedData.conversions = {
            registrations: conversionData?.registrations_total || 0,
            deposits: conversionData?.deposits_total || 0,
            conversionRate: conversionData?.registration_rate || 0,
          };
          break;
        }
        
        default:
          console.warn(`Unknown card type: ${type}`);
          break;
      }
    } catch (error) {
      console.error(`Error fetching ${type} data:`, error);
    }
    
    return { cardId, data: transformedData };
  }
);

export const resetDashboardConfig = createAsyncThunk(
  'dashboard/resetConfig',
  async (userId: number) => {
    const defaultConfig = getDefaultConfig(userId);
    
    try {
      const storedConfigs = localStorage.getItem(LOCAL_STORAGE_KEY);
      const configs = storedConfigs ? JSON.parse(storedConfigs) : {};
      configs[userId] = defaultConfig;
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(configs));
    } catch (error) {
      console.error('Error resetting dashboard config:', error);
    }
    
    return defaultConfig;
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    addCard: (state, action: PayloadAction<DashboardCardConfig>) => {
      if (state.config && state.config.cards.length < 8) {
        state.config.cards.push(action.payload);
      }
    },
    removeCard: (state, action: PayloadAction<string>) => {
      if (state.config && state.config.cards.length > 2) {
        state.config.cards = state.config.cards.filter(
          card => card.id !== action.payload
        );
        delete state.cardsData[action.payload];
        delete state.loading[action.payload];
        delete state.errors[action.payload];
      }
    },
    updateCard: (state, action: PayloadAction<DashboardCardConfig>) => {
      if (state.config) {
        const index = state.config.cards.findIndex(
          card => card.id === action.payload.id
        );
        if (index !== -1) {
          state.config.cards[index] = action.payload;
        }
      }
    },
    updateCardPosition: (
      state,
      action: PayloadAction<{ cardId: string; position: any }>
    ) => {
      if (state.config) {
        const card = state.config.cards.find(c => c.id === action.payload.cardId);
        if (card) {
          card.position = action.payload.position;
        }
      }
    },
    clearCardData: (state, action: PayloadAction<string>) => {
      delete state.cardsData[action.payload];
      delete state.loading[action.payload];
      delete state.errors[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadDashboardConfig.fulfilled, (state, action) => {
        state.config = action.payload;
      })
      .addCase(saveDashboardConfig.fulfilled, (state, action) => {
        state.config = action.payload;
      })
      .addCase(resetDashboardConfig.fulfilled, (state, action) => {
        state.config = action.payload;
        state.cardsData = {};
        state.loading = {};
        state.errors = {};
      })
      .addCase(fetchCardData.pending, (state, action) => {
        state.loading[action.meta.arg.cardId] = true;
        state.errors[action.meta.arg.cardId] = null;
      })
      .addCase(fetchCardData.fulfilled, (state, action) => {
        state.cardsData[action.payload.cardId] = action.payload.data;
        state.loading[action.payload.cardId] = false;
      })
      .addCase(fetchCardData.rejected, (state, action) => {
        state.loading[action.meta.arg.cardId] = false;
        state.errors[action.meta.arg.cardId] = 
          action.error.message || 'Не удалось загрузить данные';
      });
  },
});

export const { 
  addCard, 
  removeCard, 
  updateCard, 
  updateCardPosition,
  clearCardData 
} = dashboardSlice.actions;

export const selectDashboardConfig = (state: RootState) => state.dashboard.config;
export const selectCardData = (cardId: string) => (state: RootState) => 
  state.dashboard.cardsData[cardId];
export const selectCardLoading = (cardId: string) => (state: RootState) => 
  state.dashboard.loading[cardId];
export const selectCardError = (cardId: string) => (state: RootState) => 
  state.dashboard.errors[cardId];

export default dashboardSlice.reducer;